<template>
  <div>
    <b-card>
      <b-overlay
        :show="isLoading"
        rounded="lg"
        spinner-variant="primary"
      >
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <div class="d-flex align-items-center">
            <b-avatar
              size="64"
              variant="light-primary"
              class="mr-3"
            >
              {{ getInitials(pickupeur.username) }}
            </b-avatar>
            <div>
              <h3 class="mb-0">
                {{ pickupeur.username || 'Ramasseur' }}
              </h3>
              <p class="text-muted mb-0">
                {{ pickupeur.email }}
              </p>
              <b-badge
                :variant="pickupeur.is_active ? 'light-success' : 'light-danger'"
                pill
              >
                {{ pickupeur.is_active ? 'Actif' : 'Inactif' }}
              </b-badge>
            </div>
          </div>
          <div>
            <b-button
              variant="outline-secondary"
              class="mr-2"
              :to="{ name: 'pickupeur-list' }"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                class="mr-1"
              />
              Retour
            </b-button>
            <b-button
              variant="primary"
              :to="{ name: 'pickupeur-edit', params: { id: pickupeurId } }"
            >
              <feather-icon
                icon="EditIcon"
                class="mr-1"
              />
              Modifier
            </b-button>
          </div>
        </div>

        <b-row>
          <!-- Personal Information -->
          <b-col
            lg="6"
            md="12"
            class="mb-4"
          >
            <b-card
              title="Informations personnelles"
              class="h-100"
            >
              <b-list-group flush>
                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="UserIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Nom
                  </div>
                  <strong>{{ pickupeur.username || 'N/A' }}</strong>
                </b-list-group-item>

                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="MailIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Email
                  </div>
                  <strong>{{ pickupeur.email || 'N/A' }}</strong>
                </b-list-group-item>

                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="PhoneIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Téléphone
                  </div>
                  <strong>{{ pickupeur.phone || 'N/A' }}</strong>
                </b-list-group-item>

                <b-list-group-item
                  v-if="pickupeur.phone2"
                  class="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <feather-icon
                      icon="PhoneIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Téléphone 2
                  </div>
                  <strong>{{ pickupeur.phone2 }}</strong>
                </b-list-group-item>

                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="MapPinIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Adresse
                  </div>
                  <strong>{{ pickupeur.address || 'N/A' }}</strong>
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </b-col>

          <!-- Work Information -->
          <b-col
            lg="6"
            md="12"
            class="mb-4"
          >
            <b-card
              title="Informations de travail"
              class="h-100"
            >
              <b-list-group flush>
                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="HomeIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Dépôt
                  </div>
                  <b-badge
                    v-if="pickupeur.repository_name"
                    variant="light-info"
                    pill
                  >
                    {{ pickupeur.repository_name }}
                  </b-badge>
                  <span
                    v-else
                    class="text-muted"
                  >Non assigné</span>
                </b-list-group-item>

                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="TruckIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Véhicule
                  </div>
                  <b-badge
                    v-if="pickupeur.car_details"
                    variant="light-secondary"
                    pill
                  >
                    {{ pickupeur.car_details.brand }} {{ pickupeur.car_details.model }} - {{ pickupeur.car_details.serial_number }}
                  </b-badge>
                  <span
                    v-else
                    class="text-muted"
                  >Non assigné</span>
                </b-list-group-item>

                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="CalendarIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Date de création
                  </div>
                  <strong>{{ formatDate(pickupeur.created_at) }}</strong>
                </b-list-group-item>

                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="CheckCircleIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Statut
                  </div>
                  <b-badge
                    :variant="pickupeur.is_active ? 'light-success' : 'light-danger'"
                    pill
                  >
                    {{ pickupeur.is_active ? 'Actif' : 'Inactif' }}
                  </b-badge>
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </b-col>
        </b-row>

        <!-- Actions -->
        <b-card title="Actions">
          <b-row>
            <b-col
              md="4"
              class="mb-3"
            >
              <b-button
                variant="outline-primary"
                block
                :to="{ name: 'pickupeur-change-password', params: { id: pickupeurId } }"
              >
                <feather-icon
                  icon="LockIcon"
                  class="mr-1"
                />
                Changer mot de passe
              </b-button>
            </b-col>
            <b-col
              md="4"
              class="mb-3"
            >
              <b-button
                :variant="pickupeur.is_active ? 'outline-danger' : 'outline-success'"
                block
                @click="toggleActivation"
              >
                <feather-icon
                  :icon="pickupeur.is_active ? 'UserXIcon' : 'UserCheckIcon'"
                  class="mr-1"
                />
                {{ pickupeur.is_active ? 'Désactiver' : 'Activer' }}
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BOverlay,
  BBadge,
  BAvatar,
  BListGroup,
  BListGroupItem,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BOverlay,
    BBadge,
    BAvatar,
    BListGroup,
    BListGroupItem,
  },

  data() {
    return {
      isLoading: false,
      pickupeur: {},
    }
  },

  computed: {
    pickupeurId() {
      return this.$route.params.id
    },
  },

  created() {
    this.fetchPickupeur()
  },

  methods: {
    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },

    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },

    async fetchPickupeur() {
      this.isLoading = true
      try {
        const response = await axios.get(`/api/auth/users/pickupeurs/${this.pickupeurId}/`)
        const { data } = response

        this.pickupeur = {
          ...data,
          repository_name: data.repository_name || data.repository?.name || null,
          car_info: data.car ? `${data.car.brand || ''} - ${data.car.serial_number || ''}`.trim() : null,
        }
      } catch (error) {
        console.error('Error fetching pickupeur:', error)
        this.showToast('danger', 'Erreur lors du chargement du ramasseur')
        this.$router.push({ name: 'pickupeur-list' })
      } finally {
        this.isLoading = false
      }
    },

    async toggleActivation() {
      try {
        await axios.put(`/api/auth/users/pickupeurs/toggle-activation/${this.pickupeurId}/`)
        this.pickupeur.is_active = !this.pickupeur.is_active
        this.showToast('success', `Ramasseur ${this.pickupeur.is_active ? 'activé' : 'désactivé'} avec succès`)
      } catch (error) {
        console.error('Error toggling activation:', error)
        this.showToast('danger', 'Erreur lors du changement de statut')
      }
    },

    showToast(variant, text) {
      let title
      let icon

      if (variant === 'success') {
        title = 'Succès'
        icon = 'CheckCircleIcon'
      } else if (variant === 'warning') {
        title = 'Attention'
        icon = 'AlertTriangleIcon'
      } else {
        title = 'Erreur'
        icon = 'XCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      })
    },
  },
}
</script>
